import { motion } from 'motion/react';
import { Brain, Network, Lock, Cpu, Atom, History, Sparkles } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import { FloatingMark3D } from '@/components/viz/Chart3D';

const TILES = [
  { to: '/frontier/federated', icon: Brain, name: 'Federated Learning', desc: 'Cross-tenant classifier improvement via Flower SecAgg+ + DP-FedAvg. Coordinator never sees plaintext deltas.', tag: 'flwr_v1_secagg_plus' },
  { to: '/frontier/smpc', icon: Network, name: 'SMPC Benchmarks', desc: 'Privacy-preserving cross-tenant aggregates over Mersenne-61 field. 10-benchmark catalogue.', tag: 'k≥3 anonymity' },
  { to: '/frontier/pq', icon: Lock, name: 'Post-Quantum Vault', desc: 'NIST PQC Round-3 finalists: CRYSTALS-Kyber KEM + CRYSTALS-Dilithium signatures.', tag: 'libOQS · CIRCL · KMS' },
  { to: '/frontier/sensors', icon: Cpu, name: 'eBPF / Network Tap', desc: 'Cilium Tetragon Mode D + libpcap Mode E. Kernel ≥5.8 + SPIFFE attestation.', tag: '5 tracing policies' },
  { to: '/frontier/byo-llm', icon: Atom, name: 'BYO-LLM (Tier 5)', desc: '9-provider catalogue: Anthropic, Bedrock-Anthropic, Vertex-Anthropic, Azure OpenAI, OpenAI, Mistral, Cohere, vLLM, custom.', tag: 'Customer-VPC KMS keys' },
  { to: '/frontier/replay', icon: History, name: 'Compliance Replay', desc: 'Counter-factual: "what if RBI 6h applied 90 days earlier?" 15-regulator catalogue.', tag: '15 regulators' },
];

export function FrontierOverview() {
  return (
    <PageContainer>
      <PageHeader
        title="Frontier"
        subtitle="Sovereign-tier capabilities for the most demanding privacy + security workloads. Federated learning, SMPC, post-quantum crypto, eBPF, and Tier 5 BYO-LLM."
        badges={[{ label: 'Sovereign', variant: 'warning' }, { label: 'Phase 5', variant: 'info' }]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center mb-8">
        <div className="space-y-4">
          <h2 className="text-3xl font-display font-bold tracking-tight">
            <span className="bg-gradient-to-r from-chart-2 via-chart-7 to-chart-1 bg-clip-text text-transparent">
              Frontier-tier privacy at sovereign scale.
            </span>
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            The Frontier module bundles capabilities most enterprises won't need today - and won't be able to live
            without in 24 months. Federated learning across tenants. SMPC for cross-tenant benchmarks without ever
            seeing per-tenant inputs. NIST Post-Quantum Round-3 finalists protecting your audit chain against
            quantum-class adversaries. Customer-VPC BYO-LLM with KMS-only key custody.
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="glass">SecAgg+ DP-FedAvg</Badge>
            <Badge variant="glass">Mersenne-61 SMPC</Badge>
            <Badge variant="glass">CRYSTALS-Kyber/Dilithium</Badge>
            <Badge variant="glass">Cilium Tetragon</Badge>
            <Badge variant="glass">9 LLM providers</Badge>
            <Badge variant="glass">15 regulators replay</Badge>
          </div>
        </div>
        <FloatingMark3D height={360} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="FL rounds completed" value={47} icon={Brain} />
        <KpiCard label="SMPC sessions (24h)" value={3} icon={Network} />
        <KpiCard label="PQ-signed audit blocks" value={184_502} icon={Lock} />
        <KpiCard label="eBPF sensors active" value={12} icon={Cpu} hint="across 4 K8s clusters" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {TILES.map((t, i) => {
          const Icon = t.icon;
          return (
            <motion.div
              key={t.to}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link to={t.to}>
                <GlassCard shine className="p-5 h-full lift cursor-pointer">
                  <div className="flex items-start justify-between mb-3">
                    <div className="h-10 w-10 rounded-md bg-gradient-to-br from-chart-2/30 to-chart-7/30 flex items-center justify-center text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <Sparkles className="h-3 w-3 text-warning" />
                  </div>
                  <CardTitle className="text-base">{t.name}</CardTitle>
                  <CardDescription className="text-xs mt-1 mb-3 leading-snug">{t.desc}</CardDescription>
                  <Badge variant="glass" className="text-[9px]">{t.tag}</Badge>
                </GlassCard>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </PageContainer>
  );
}
